import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import LessonBooking from './LessonBooking';
import theme from '../theme';
import { isUserAdmin } from '../utils/adminUtils';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  will-change: opacity;
`;

const PopupContainer = styled(motion.div)`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border-radius: ${props => props.theme.borderRadius.large};
  padding: 60px;
  max-width: 700px;
  width: 100%;
  max-height: 85vh;
  min-height: ${props => props.isDatePickerFocused ? '460px' : '390px'};
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(205, 255, 155, 0.3);
  position: relative;
  will-change: opacity, transform;
  transition: min-height 0.3s ease;
  border: 2px solid ${props => props.theme.colors.accent1};
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, ${props => props.theme.colors.secondary} 0%, #e8316d 100%);
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(219, 39, 98, 0.3);

  &:hover {
    background: linear-gradient(135deg, #e8316d 0%, ${props => props.theme.colors.secondary} 100%);
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(219, 39, 98, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const PopupTitle = styled.h2`
  font-size: 28px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.secondary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 700;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    border-radius: 2px;
  }
`;

const PopupContent = styled.div`
  text-align: center;
  color: ${props => props.theme.colors.secondary};
  font-size: 16px;
  line-height: 1.6;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent 0%, ${props => props.theme.colors.accent1} 50%, transparent 100%);
  }
`;

const WarningMessage = styled.div`
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: 16px;
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 1.5;
`;

const RescheduleLessonPopup = ({ isOpen, onClose, onLessonRescheduled, lessonId, currentLesson, title = "Stunde verschieben", availableBudgets = [] }) => {
  const [isDatePickerFocused, setIsDatePickerFocused] = useState(false);

  const handleDatePickerFocus = () => {
    setIsDatePickerFocused(true);
  };

  const handleDatePickerBlur = () => {
    setIsDatePickerFocused(false);
  };

  // Check if this is the last allowed reschedule (2 reschedules = 3rd attempt)
  const isLastReschedule = currentLesson && currentLesson.rescheduleCount >= 2 && (currentLesson.status === 'CONFIRMED' || currentLesson.status === 'SCHEDULING');

  // Extract warning message logic into functions
  const getInheritedRescheduleText = (inheritedCount) => {
    if (inheritedCount <= 0) return '';
    
    const plural = inheritedCount > 1;
    const verb = plural ? 'wurden' : 'wurde';
    const suffix = plural ? 'en' : '';
    
    return ` (${inheritedCount} Verschiebung${suffix} ${verb} von vorherigen Stunden übernommen)`;
  };

  const getWarningMessage = () => {
    // Admin users don't need warning messages
    if (isUserAdmin()) return null;
    
    if (!isLastReschedule) return null;

    const inheritedText = getInheritedRescheduleText(currentLesson.inheritedRescheduleCount);
    const message = 'Dies ist deine letzte Möglichkeit, diese Stunde zu verschieben. Nach dieser Verschiebung kannst du die Stunde nicht mehr umplanen' + inheritedText;

    return (
      <WarningMessage>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <div>⚠️</div>
          <div style={{ flex: 1 }}>
            {message}
          </div>
        </div>
      </WarningMessage>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          onClick={onClose}
        >
          <PopupContainer
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            isDatePickerFocused={isDatePickerFocused}
          >
            <CloseButton onClick={onClose}>
              ×
            </CloseButton>
            <PopupTitle>{title}</PopupTitle>
            <PopupContent>
              {getWarningMessage()}
              <LessonBooking 
                onDatePickerFocus={handleDatePickerFocus}
                onDatePickerBlur={handleDatePickerBlur}
                onClose={onClose}
                onLessonBooked={onLessonRescheduled}
                availableBudgets={availableBudgets}
                isRescheduling={true}
                lessonId={lessonId}
                currentLesson={currentLesson}
                isAdmin={isUserAdmin()}
              />
            </PopupContent>
          </PopupContainer>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default RescheduleLessonPopup; 